import axios from 'axios';
import { getFingerprintForHeader, syncFingerprintFromCookie } from './clientAuth';
import {
    buildNativeIdHeaders,
    exportSessionToNative,
    getBrowserSessionProtocolPayload,
    getStoredNativeAccessToken,
    getStoredNativeRefreshToken,
    persistNativeSession,
    refreshNativeSession,
} from './nativeSession';

const API_URL = process.env.REACT_APP_SUPPORT_API_URL || '';
const API_URL_ID = process.env.REACT_APP_IDP_ORIGIN || '';
const ID_WEB_URL = process.env.REACT_APP_ID_WEB_URL || '';
const LOGIN_URL = process.env.REACT_APP_ID_LOGIN_URL || (ID_WEB_URL ? `${ID_WEB_URL.replace(/\/$/, '')}/login` : '/login');

function redirectToLogin() {
    try {
        const returnTo = encodeURIComponent(window.location.href);
        window.location.replace(`${LOGIN_URL}?returnTo=${returnTo}`);
    } catch {
        window.location.replace(LOGIN_URL);
    }
}

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
});

api.interceptors.request.use(
    (config) => {
        const fp = getFingerprintForHeader();
        if (fp) {
            config.headers = config.headers || {};
            config.headers['X-CSRF-Token'] = fp;
        }
        const accessToken = getStoredNativeAccessToken();
        if (accessToken) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function refreshSessionOnce() {
    const fp = getFingerprintForHeader();
    return refreshNativeSession(fp)
        .then((response) => {
            syncFingerprintFromCookie();
            return response;
        });
}

export function refreshRolesJwt() {
    return refreshSessionOnce()
        .catch(() => null)
        .then(() => {
            const fp = getFingerprintForHeader();
            const headers = buildNativeIdHeaders(fp, { includeAccessToken: true });

            return axios
                .post(
                    `${API_URL_ID}/api/roles/refresh`,
                    getBrowserSessionProtocolPayload(),
                    {
                        withCredentials: true,
                        headers,
                    }
                )
                .then((response) => {
                    syncFingerprintFromCookie();
                    const data = response?.data || {};
                    persistNativeSession(data);
                    exportSessionToNative({
                        access_token: data?.access_token || data?.token || '',
                        refresh_token: getStoredNativeRefreshToken(),
                        fp: fp || '',
                        src: 'support_roles_refresh',
                    });
                    return data;
                });
        });
}

api.interceptors.response.use(
    (response) => {
        syncFingerprintFromCookie();
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (!error.response || !originalRequest) {
            return Promise.reject(error);
        }

        syncFingerprintFromCookie();

        if (error.response.status !== 401) {
            return Promise.reject(error);
        }

        const url = String(originalRequest.url || '');

        if (url.includes('/api/refresh') || url.includes('/api/logout')) {
            redirectToLogin();
            return Promise.reject(error);
        }

        if (originalRequest._retry) {
            redirectToLogin();
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        try {
            await refreshSessionOnce();
            return api(originalRequest);
        } catch (refreshError) {
            const status = refreshError?.response?.status;
            const code = refreshError?.response?.data?.error;

            if (
                !status ||
                status === 401 ||
                code === 'invalid_or_expired' ||
                code === 'no_refresh' ||
                code === 'fingerprint_mismatch' ||
                code === 'token_reused'
            ) {
                redirectToLogin();
            }

            return Promise.reject(refreshError);
        }
    }
);

export default api;
